import { Link, useMatch, type ErrorComponentProps } from "@tanstack/react-router";

import { cn } from "../lib/cn";
import { LogoIcon } from "./icons";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Skeleton } from "./ui/skeleton";

/**
 * What a route shows once it has loaded for longer than the router's `defaultPendingMs`. Inside the app shell it keeps
 * the rail and top bar and shows a page skeleton. Signed-out pages render without the shell, so they get the splash.
 */
export function DefaultPendingComponent() {
	const isPublic = useMatch({ strict: false, select: match => match.staticData.public === true });
	return isPublic ? <AppSplash /> : <PageSkeleton />;
}

/**
 * The Ravnar bird breathing on the brand ground, filling the screen. The root route shows it while the app starts,
 * before the shell exists.
 */
export function AppSplash() {
	return (
		<div role="status" className="page-ground flex h-dvh animate-fade-in items-center justify-center">
			<span className="sr-only">Loading Ravnar…</span>
			<LogoIcon className="h-10 w-10 animate-breathe text-ink motion-reduce:animate-none" />
		</div>
	);
}

const SKELETON_ROW_WIDTHS = ["w-2/5", "w-1/3", "w-1/2", "w-1/4"] as const;

/** The shape most pages have, a header and a card of rows, shimmering where the page will be. */
function PageSkeleton() {
	return (
		<div role="status" className="animate-fade-in">
			<span className="sr-only">Loading page…</span>
			<Skeleton className="mb-3 h-8 w-56 rounded-btn" />
			<Skeleton className="mb-5 h-3.5 w-full max-w-[420px]" />
			<Card className="max-w-[880px]">
				<div className="border-b border-line px-4 py-4">
					<Skeleton className="h-2.5 w-24" />
				</div>
				{SKELETON_ROW_WIDTHS.map(width => (
					<div key={width} className="flex items-center gap-3 border-b border-line px-4 py-3.5 last:border-0">
						<Skeleton className="size-2 rounded-pill" />
						<Skeleton className={cn("h-3", width)} />
						<Skeleton className="ml-auto h-3 w-14" />
					</div>
				))}
			</Card>
		</div>
	);
}

export function DefaultErrorComponent({ error, reset }: ErrorComponentProps) {
	return (
		<div role="alert" className="max-w-[880px] rounded-card border border-alert-mid bg-alert-soft p-4 text-alert">
			<h2 className="text-body font-semibold">Something went wrong</h2>
			<p className="mt-1 text-caption">{error instanceof Error ? error.message : String(error)}</p>
			<Button className="mt-3" onClick={reset}>
				Try again
			</Button>
		</div>
	);
}

export function NotFound() {
	return (
		<Card className="max-w-[880px] p-4">
			<h1 className="text-title font-semibold tracking-title text-ink">Page not found</h1>
			<p className="mt-1 text-body text-ink-2">The page you asked for does not exist.</p>
			<Link to="/" className="mt-3 inline-block text-body text-accent hover:underline">
				Go home
			</Link>
		</Card>
	);
}
